import { API } from "aws-amplify";
import { tokenManager } from "../services/tokenManager";

export interface CustomerUsageData {
  id: string;
  name: string;
  status: string;
  dataResidency: string;
  numberOfCallsMade: number;
  lastCallMadeAt: string;
  loadSheddingTier: number;
  createdAt: string;
  lastReplenishedAt: string;
  nextReplenishAt: string;
  usageTierId: string;
  standardTokensRemaining: number;
  premiumTokensRemaining: number;
  superPremiumTokensRemaining: number;
  reasoningTokensRemaining: number;
  standardCallsRemaining: number;
  premiumCallsRemaining: number;
  superPremiumCallsRemaining: number;
  reasoningCallsRemaining: number;
  standardTokenUnlimited: boolean;
  premiumTokenUnlimited: boolean;
  superPremiumTokenUnlimited: boolean;
  reasoningTokenUnlimited: boolean;
  standardCallUnlimited: boolean;
  premiumCallUnlimited: boolean;
  superPremiumCallUnlimited: boolean;
  reasoningCallUnlimited: boolean;
  usageLimitAction: string;
  nextUsageTierId: string;
  nextUsageTierAt: string;
  usageTier?: {
    id: string;
    projectId: string;
    status: string;
    name: string;
    description: string;
    version: number;
    activeAt: string;
    inactiveAt: string;
    priorUsageTierId: string;
    baseUsageTierId: string;
    replenishmentPeriod: string;
    replenishmentMethod: string;
    usageLimitAction: string;
    standardTokenReplenishmentAmount: number;
    premiumTokenReplenishmentAmount: number;
    superPremiumTokenReplenishmentAmount: number;
    reasoningTokenReplenishmentAmount: number;
    standardCallReplenishmentAmount: number;
    premiumCallReplenishmentAmount: number;
    superPremiumCallReplenishmentAmount: number;
    reasoningCallReplenishmentAmount: number;
    standardTokenUnlimited: boolean;
    premiumTokenUnlimited: boolean;
    superPremiumTokenUnlimited: boolean;
    reasoningTokenUnlimited: boolean;
    standardCallUnlimited: boolean;
    premiumCallUnlimited: boolean;
    superPremiumCallUnlimited: boolean;
    reasoningCallUnlimited: boolean;
    createdAt: string;
  };
}

export interface CustomerUsageResponse {
  data: CustomerUsageData[];
}

/**
 * Fetches customer usage data from the LLM Public API
 * Uses the existing /customers/{id}/usage endpoint which returns customer + usage tier data
 * 
 * @param projectId - The project ID (used for API key authentication, not needed for JWT)
 * @param customerId - The customer ID (URL encoded)
 * @param apiKey - The API key for authentication (optional if jwtToken provided)
 * @param jwtToken - The JWT token for authentication (optional if apiKey provided)
 * @returns Promise with customer usage data including usage tier information
 */
export async function fetchCustomerUsage(
  projectId: string,
  customerId: string,
  apiKey?: string,
  jwtToken?: string
): Promise<CustomerUsageResponse> {
  
  // URL encode the customer ID to handle email addresses and special characters
  const encodedCustomerId = encodeURIComponent(customerId.replace(projectId, ''));

  // Build headers based on authentication method
  const headers: Record<string, string> = {};
  
  if (jwtToken) {
    // Use JWT token authentication
    headers['Authorization'] = `Bearer ${jwtToken}`;
  } else if (apiKey) {
    // Use API key authentication
    headers['x-api-key'] = apiKey;
    headers['x-project-id'] = projectId;
  } else {
    throw new Error('Either apiKey or jwtToken must be provided');
  }
  
  try {
    const response = await API.get("llmpublicapi", `/customers/${encodedCustomerId}/usage`, {
      headers,
    });

    return { data: response };
  } catch (error) {
    console.error("Error fetching customer usage:", error);
    throw error;
  }
}

/**
 * Fetches customer usage data using the current user context
 * This is a convenience wrapper for authenticated admin portal usage
 */
export async function fetchCustomerUsageWithContext(
  customerId: string,
  projectId: string,
  apiKey?: string,
  jwtToken?: string
): Promise<CustomerUsageData | null> {
  try {
    const response = await fetchCustomerUsage(projectId, customerId, apiKey, jwtToken);
    return response.data?.[0] || null;
  } catch (error) {
    console.error("Error fetching customer usage with context:", error);
    return null;
  }
}

/**
 * Enhanced version of fetchCustomerUsage with automatic token refresh capability
 * Automatically handles token expiration and refresh for JWT authentication
 * 
 * @param projectId - Project identifier (optional for JWT auth)
 * @param customerId - Customer identifier
 * @param apiKey - Optional API key for authentication
 * @param jwtToken - Optional JWT token for authentication
 * @returns Promise with customer usage data including usage tier information
 */
export async function fetchCustomerUsageWithRefresh(
  projectId: string,
  customerId: string,
  apiKey?: string,
  jwtToken?: string
): Promise<CustomerUsageResponse> {
  
  // URL encode the customer ID to handle email addresses and special characters
  // When using JWT authentication, projectId is extracted from the token by the backend
  let cleanCustomerId = customerId;
  if (projectId && customerId.startsWith(projectId)) {
    // Remove projectId prefix if present and we have a projectId (API key auth)
    cleanCustomerId = customerId.replace(projectId, '');
  }
  const encodedCustomerId = encodeURIComponent(cleanCustomerId);

  // Build headers based on authentication method
  const headers: Record<string, string> = {};
  
  if (jwtToken) {
    // Initialize token manager if we have a token
    if (!tokenManager.getCurrentToken()) {
      tokenManager.setToken({
        token: jwtToken,
        expiresAt: new Date(Date.now() + 3600000).toISOString(), // Assume 1 hour expiry
        expiresIn: 3600
      });
    }
    
    // Use JWT token authentication with automatic refresh
    const validToken = await tokenManager.getValidToken();
    if (validToken) {
      headers['Authorization'] = `Bearer ${validToken}`;
    } else {
      // Fallback to provided token if manager doesn't have one
      headers['Authorization'] = `Bearer ${jwtToken}`;
    }
  } else if (apiKey && projectId) {
    // Use API key authentication (requires projectId)
    headers['x-api-key'] = apiKey;
    headers['x-project-id'] = projectId;
  } else {
    throw new Error('Either jwtToken or both apiKey and projectId must be provided');
  }
  
  try {
    const response = await API.get("llmpublicapi", `/customers/${encodedCustomerId}/usage`, {
      headers,
    });

    return { data: response };
  } catch (error: any) {
    // Handle token expiration specifically for JWT auth
    if (error.response?.status === 401 && jwtToken) {
      console.warn("Token may be expired, attempting refresh...");
      
      // Try to refresh token and retry request
      const refreshResult = await tokenManager.refreshToken();
      if (refreshResult.success && refreshResult.token) {
        headers['Authorization'] = `Bearer ${refreshResult.token}`;
        
        try {
          const retryResponse = await API.get("llmpublicapi", `/customers/${encodedCustomerId}/usage`, { 
            headers 
          });
          return { data: retryResponse };
        } catch (retryError) {
          console.error("Retry after token refresh failed:", retryError);
          throw retryError;
        }
      }
    }
    
    console.error("Error fetching customer usage:", error);
    throw error;
  }
}
